/**
 * Keeps the wishlist hearts in the state of the wishlist.
 *
 * The wishlist app (Wishlist Engine) only updates the heart that was clicked. A page can show the
 * same item more than once (product page: heart next to the title on desktop and in the buy area
 * on mobile, plus product cards), so the other hearts kept their old state until the next page
 * load, and a click on such a heart sent the opposite action. When the app switches the state of
 * one heart (data-added), the other hearts of the same product and variant get the same state and
 * icon. With the app setting "wishlist per product" the variant is not compared.
 *
 * The app keeps the list of saved items in its cookie WISHLIST_PRODUCTS_IDS and reads it only when
 * the page loads. When the browser shows a page again from its back/forward cache, or when the list
 * was changed in another tab, the hearts still show the old state. A click on such a heart makes
 * the app stop halfway: it reports "removed" and then puts the old heart back. Whenever the page is
 * shown again, its tab becomes visible or its window gets the focus, the hearts are set to the
 * state in the cookie. A click on a heart that still shows an old state only corrects the hearts.
 */
(() => {
  const HEART_SELECTOR = '.wishlist-engine';

  /** @returns {any} */
  const app = () => /** @type {any} */ (window);

  const isVariantLevel = () =>
    String(app().app_widget_data?.is_enable_wishlist_variant_level ?? '1') === '1';

  /** @param {HTMLElement} heart */
  const isDrawn = (heart) =>
    Boolean(heart.firstElementChild) &&
    (heart.dataset.added === 'true' || heart.dataset.added === 'false');

  /**
   * @param {HTMLElement} source
   * @param {HTMLElement} heart
   */
  const isSameItem = (source, heart) =>
    heart.dataset.product_id === source.dataset.product_id &&
    (!isVariantLevel() || heart.dataset.variant_id === source.dataset.variant_id);

  /** The app's loader object, once the app has loaded its settings. */
  const getLoader = () => {
    const loader = app().app_widget_load;
    const settingsLoaded = 'is_enable_app' in (app().app_widget_data ?? {});
    return loader && typeof loader._getCookie === 'function' && settingsLoaded ? loader : null;
  };

  /**
   * Items on the wishlist as the app stores them: { [productId]: { variants: number[] } }.
   * Null as long as the app has not loaded the list.
   * @param {any} loader
   */
  const getSavedItems = (loader) => {
    if (loader._getCookie('WISHLIST_PRODUCTS_IDS_SET') !== '1') {
      return null;
    }
    try {
      const items = JSON.parse(loader._getCookie('WISHLIST_PRODUCTS_IDS') || '{}');
      return items && typeof items === 'object' ? items : null;
    } catch (error) {
      return null;
    }
  };

  /**
   * @param {HTMLElement} heart
   * @param {any} items
   */
  const isSaved = (heart, items) => {
    const item = items[heart.dataset.product_id ?? ''];
    if (!item) {
      return false;
    }
    if (!isVariantLevel()) {
      return true;
    }
    return (
      Array.isArray(item.variants) &&
      item.variants.includes(parseInt(heart.dataset.variant_id ?? '', 10))
    );
  };

  /**
   * Draws the icon and label the app uses for this state. Returns false when the heart has no icon.
   * @param {HTMLElement} heart
   * @param {boolean} saved
   */
  const drawState = (heart, saved) => {
    const {
      heartIcon,
      heartFillIcon,
      wishlist_widget: widget,
      shopify_local_language: language,
    } = app();
    const iconSlot = heart.querySelector('.wishlist-icon');
    if (!iconSlot || typeof heartIcon !== 'string' || typeof heartFillIcon !== 'string') {
      return false;
    }

    // Same rule as the app: hearts with their own styling use the icon as it is,
    // the others get the text colour from the app settings.
    const ownStyling = heart.dataset.css !== 'true' && Boolean(heart.dataset.css);
    const color = widget?.wishlistButtonTextColor;
    if (ownStyling) {
      iconSlot.innerHTML = saved ? heartFillIcon : heartIcon;
    } else {
      iconSlot.innerHTML = saved
        ? heartFillIcon.replaceAll('currentColor', color)
        : heartIcon.replace('currentColor', color);
    }

    const label = (saved ? widget?.wishlistButtonAfterText : widget?.wishlistButtonBeforeText)?.[
      language
    ];
    if (label) {
      heart.setAttribute('aria-label', label);
      const text = heart.querySelector('.wishlist-text');
      if (text) {
        text.textContent = label;
      }
    }
    heart.dataset.added = String(saved);
    return true;
  };

  /**
   * Sets all drawn hearts and the header counter to the state in the cookie.
   * Returns the saved items, or null when the app has not loaded the list yet.
   */
  const syncWithSavedItems = () => {
    const loader = getLoader();
    const items = loader ? getSavedItems(loader) : null;
    if (!items) {
      return null;
    }
    const productId = String(app().ShopifyAnalytics?.meta?.product?.id ?? '');

    document.querySelectorAll(HEART_SELECTOR).forEach((heart) => {
      if (!(heart instanceof HTMLElement) || !isDrawn(heart)) {
        return;
      }
      const saved = isSaved(heart, items);
      if (String(saved) !== heart.dataset.added) {
        drawState(heart, saved);
      }
      // The app shows the hearts of the product page from this list when another variant is selected
      if (
        productId &&
        heart.dataset.product_id === productId &&
        heart.dataset.variantsIds !== undefined
      ) {
        const variants = JSON.stringify(items[productId]?.variants ?? []);
        if (heart.dataset.variantsIds !== variants) {
          heart.dataset.variantsIds = variants;
        }
      }
    });

    // Header counter: the app stores the number of items next to the list
    const total = parseInt(loader._getCookie('WISHLIST_TOTAL'), 10);
    const counter = document.querySelector('.wishlist-total-count');
    if (
      !Number.isNaN(total) &&
      counter instanceof HTMLElement &&
      counter.dataset.total !== String(total) &&
      typeof loader._setWishlistTotalCount === 'function'
    ) {
      loader._setWishlistTotalCount(total);
    }
    return items;
  };

  /** @param {HTMLElement} source */
  const syncFrom = (source) => {
    const added = source.dataset.added;
    // Only take over a state the app has drawn
    if (!isDrawn(source)) {
      return;
    }

    document.querySelectorAll(HEART_SELECTOR).forEach((heart) => {
      if (!(heart instanceof HTMLElement) || heart === source || heart.dataset.added === added) {
        return;
      }
      if (!isSameItem(source, heart)) {
        return;
      }

      heart.dataset.added = added;
      heart.innerHTML = source.innerHTML;
      const label = source.getAttribute('aria-label');
      if (label) {
        heart.setAttribute('aria-label', label);
      }
    });
  };

  new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      const heart = mutation.target;
      if (heart instanceof HTMLElement && heart.matches(HEART_SELECTOR)) {
        syncFrom(heart);
      }
    });
  }).observe(document.documentElement, {
    subtree: true,
    attributes: true,
    attributeFilter: ['data-added'],
  });

  window.addEventListener('pageshow', (event) => {
    if (event.persisted) {
      syncWithSavedItems();
    }
  });
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      syncWithSavedItems();
    }
  });
  window.addEventListener('focus', () => {
    syncWithSavedItems();
  });

  // Runs before the app's own click handler on the heart. A heart that still shows an old state is
  // only corrected; the click does not reach the app, which would otherwise send the opposite action.
  document.addEventListener(
    'click',
    (event) => {
      const heart = event.target instanceof Element ? event.target.closest(HEART_SELECTOR) : null;
      if (!(heart instanceof HTMLElement) || !isDrawn(heart)) {
        return;
      }
      const loader = getLoader();
      const items = loader ? getSavedItems(loader) : null;
      if (!items || String(isSaved(heart, items)) === heart.dataset.added) {
        return;
      }
      syncWithSavedItems();
      if (String(isSaved(heart, items)) === heart.dataset.added) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    },
    true
  );
})();
